import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IPreSignedUploadRequest,
  IRemoveFilePathTrade,
  IUpdateTradeinGalleryRequest,
} from '@app/shared/interfaces';
import { HttpService, ResponseType } from '@app/shared/services/http/http.service';
import { UrlService } from '@app/shared/services/url/url.service';
import { ITradeGalleryResponse } from '@app/tvf/models/trade-gallery-response';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, switchMap, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  constructor(
    private _urlService: UrlService,
    private _http: HttpClient,
    private _httpService: HttpService,
    private _toastrService: ToastrService
  ) {}

  upload(options: {
    preSignedRequest: IPreSignedUploadRequest;
    preSignedApiService: string;
    file: any;
    updateTradeGalleryRequest: IUpdateTradeinGalleryRequest;
    updateTradeGalleryApiService: string;
    returnMessage: {
      success: string;
      error: string;
    };
  }) {
    return this.postGeneratePresignedUrlUpload(
      options.preSignedRequest,
      options.preSignedApiService
    ).pipe(
      switchMap((preSignedResponse: { url: string }) =>
        this.uploadFile(options.file, preSignedResponse.url)
      ),
      switchMap(() =>
        this.updateTradeGallery(
          options.updateTradeGalleryRequest,
          options.updateTradeGalleryApiService
        )
      ),
      tap(() => {
        this._toastrService.success(options.returnMessage.success);
      }),
      catchError(() => {
        this._toastrService.error(options.returnMessage.error);
        return throwError(() => new Error('erro inesperado, tente novamente'));
      })
    );
  }

  postGeneratePresignedUrlUpload(payload: IPreSignedUploadRequest, urlApiService: string) {
    if (payload.filename && payload.idTrade && payload.contentType) {
      const url = this._urlService.getUrl(urlApiService, {});
      return this._httpService.post(url, payload, ResponseType.JSON);
    }
    return throwError(() => new Error('Erro ao iniciar o upload, tente novamente.'));
  }

  uploadFile(file: File, url: string, options?: any) {
    const blob = new Blob([file], { type: file.type });
    const headers = {
      'Content-Type': file.type,
      ...(options?.headers ?? {}),
    };
    return this._http.put(url, blob, {
      headers,
      responseType: 'text',
      params: {
        mimeType: file.type,
      },
    });
  }

  updateTradeGallery(
    payload: IUpdateTradeinGalleryRequest,
    urlApiService: string,
    options?: { headers?: Record<string, string> }
  ) {
    if (payload.filename && payload.idTrade) {
      const url = this._urlService.getUrl(urlApiService, {});
      return this._httpService.post(url, payload, options?.headers ?? {}, ResponseType.JSON);
    }
    return throwError(() => new Error('Erro ao finalizar o upload do arquivo, tente novamente.'));
  }

  removeFilePathByTrade(payload: IRemoveFilePathTrade, urlApiService: string): Observable<any> {
    const url = this._urlService.getUrl(urlApiService, {});
    return this._http.delete(url, { body: payload }).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }

  getGalleryByTradeId(idTrade: string, urlSource: string): Observable<ITradeGalleryResponse> {
    const url = this._urlService.getUrl(urlSource, { idTrade });
    return this._httpService.get(url, idTrade);
  }

  convertSrcToFile(src: string, fileName: string): Promise<File | null> {
    return new Promise((resolve, reject) => {
      if (src.startsWith('data:')) {
        const arr = src.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
          u8arr[i] = bstr.charCodeAt(i);
        }
        resolve(new File([u8arr], fileName, { type: mime }));
      } else {
        fetch(src)
          .then((response) => response.blob())
          .then((blob) => {
            resolve(new File([blob], fileName, { type: blob.type }));
          })
          .catch((error) => {
            reject(`Erro ao converter URL para File: ${error}`);
          });
      }
    });
  }
}
